/**
 * Cookie Consent Configuration
 * Using vanilla-cookieconsent library
 * @see https://cookieconsent.orestbida.com/
 */

const translations = {
  'en': {
    consentModal: {
      title: 'We use cookies',
      description: 'We use cookies to ensure the site works properly, personalize content, and improve performance.',
      acceptAllBtn: 'Accept all',
      acceptNecessaryBtn: 'Reject non-essential',
      showPreferencesBtn: 'Manage preferences',
    },
    preferencesModal: {
      title: 'Cookie preferences',
      acceptAllBtn: 'Accept all',
      acceptNecessaryBtn: 'Reject non-essential',
      savePreferencesBtn: 'Save preferences',
      closeIconLabel: 'Close',
      sections: [
        {
          title: 'Cookie usage',
          description: 'We use cookies to help our website work properly, improve performance, and understand how visitors use our site. You can choose which cookies to allow.'
        },
        {
          title: 'Necessary cookies',
          description: 'These cookies are required for the website to function and cannot be switched off. They help with basic features like security and page navigation.',
          linkedCategory: 'necessary'
        },
        {
          title: 'Analytics cookies',
          description: 'Analytics cookies show us how people interact with our website. They help us track metrics like visitor numbers, traffic sources, and which pages are most viewed.',
          linkedCategory: 'analytics'
        },
        {
          title: 'Advertising cookies',
          description: 'Advertising cookies personalize ads for visitors based on browsing activity and help analyze campaign results.',
          linkedCategory: 'advertising'
        }
      ]
    }
  },
  'es': {
    consentModal: {
      title: 'Usamos cookies',
      description: 'Usamos cookies para garantizar que el sitio funcione correctamente, personalizar el contenido y mejorar el rendimiento.',
      acceptAllBtn: 'Aceptar todas',
      acceptNecessaryBtn: 'Rechazar las no esenciales',
      showPreferencesBtn: 'Gestionar preferencias',
    },
    preferencesModal: {
      title: 'Preferencias de cookies',
      acceptAllBtn: 'Aceptar todas',
      acceptNecessaryBtn: 'Rechazar las no esenciales',
      savePreferencesBtn: 'Guardar preferencias',
      closeIconLabel: 'Cerrar',
      sections: [
        {
          title: 'Uso de cookies',
          description: 'Usamos cookies para ayudar a que nuestro sitio web funcione correctamente, mejorar el rendimiento y comprender cómo los visitantes utilizan el sitio. Puede elegir qué cookies permitir.'
        },
        {
          title: 'Cookies necesarias',
          description: 'Estas cookies son necesarias para que el sitio web funcione y no se pueden desactivar. Ayudan con funciones básicas como la seguridad y la navegación por las páginas.',
          linkedCategory: 'necessary'
        },
        {
          title: 'Cookies de análisis',
          description: 'Las cookies de análisis nos muestran cómo las personas interactúan con nuestro sitio web. Nos ayudan a medir datos como el número de visitantes, las fuentes de tráfico y las páginas más vistas.',
          linkedCategory: 'analytics'
        },
        {
          title: 'Cookies publicitarias',
          description: 'Las cookies publicitarias personalizan los anuncios para los visitantes según su actividad de navegación y ayudan a analizar los resultados de las campañas.',
          linkedCategory: 'advertising'
        }
      ]
    }
  }
};

/**
 * Update gtag consent state based on accepted categories
 */
function updateGtagConsent(categories) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  // Analytics consent
  const analyticsGranted = categories.includes('analytics');
  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied'
  });

  // Advertising consent
  const advertisingGranted = categories.includes('advertising');
  window.gtag('consent', 'update', {
    ad_storage: advertisingGranted ? 'granted' : 'denied',
    ad_user_data: advertisingGranted ? 'granted' : 'denied',
    ad_personalization: advertisingGranted ? 'granted' : 'denied'
  });
}

/**
 * Push consent events to dataLayer for GTM
 */
function pushConsentToDataLayer(categories, changedCategories = null) {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];

  const event = {
    event: 'cookie_consent_update',
    analytics_consent: categories.includes('analytics') ? 'granted' : 'denied',
    advertising_consent: categories.includes('advertising') ? 'granted' : 'denied',
  };

  if (changedCategories) {
    event.changed_categories = changedCategories;
  }

  window.dataLayer.push(event);

  // Push individual consent events for analytics
  if (categories.includes('analytics')) {
    window.dataLayer.push({ event: 'consent_accepted_analytics' });
  }
  if (categories.includes('advertising')) {
    window.dataLayer.push({ event: 'consent_accepted_advertising' });
  }
}

/**
 * Get cookie consent configuration for a given locale
 * @param {string} locale - The current locale (e.g., 'en-uk', 'es-us')
 * @param {boolean} autoShow - Whether to auto-show the banner (default: true)
 * @returns {object} - Configuration object for vanilla-cookieconsent
 */
export function getConsentConfig(locale, autoShow = false) {
  // Map locale to translation key
  const translationKey = locale.startsWith('es') ? 'es' : 'en';

  return {
    autoShow,

    // Cookie settings
    cookie: {
      name: 'cc_cookie',
      expiresAfterDays: 365,
    },

    // GUI options
    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom left',
        flipButtons: false,
        equalWeightButtons: true
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
        equalWeightButtons: true
      }
    },

    // Categories
    categories: {
      necessary: {
        enabled: true,
        readOnly: true  // Cannot be toggled off
      },
      analytics: {
        enabled: false,  // Default off
        readOnly: false
      },
      advertising: {
        enabled: false, // Default off per requirements
        readOnly: false
      }
    },

    // Language/translations
    language: {
      default: translationKey,
      translations: translations
    },

    // Callbacks for gtag consent mode integration
    onFirstConsent: ({ cookie }) => {
      updateGtagConsent(cookie.categories);
      pushConsentToDataLayer(cookie.categories);
    },

    onConsent: ({ cookie }) => {
      updateGtagConsent(cookie.categories);
    },

    onChange: ({ changedCategories, cookie }) => {
      updateGtagConsent(cookie.categories);
      pushConsentToDataLayer(cookie.categories, changedCategories);
    }
  };
}
